import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import type { Phase } from '../../types';
import type { FeatureSearchHit, FeatureWithStatus } from '../../types/acceptance';
import { featureApi } from '../../api/endpoints';
import { BugHistoryIcon } from '../../components/BugHistoryIcon';
import { FEATURE_STATUS_META } from '../Admin/statusMeta';
import { FEATURE_FIXING_META } from '../Bugs/statusMeta';

interface PhaseFeatureTreeProps {
  projectId: string;
  phases: Phase[];
  featuresByPhase: Record<string, FeatureWithStatus[]>;
  loadingPhases: boolean;
  loadingFeatures: Record<string, boolean>;
  errorByPhase: Record<string, string | null>;
  openPhaseIds: string[];
  onTogglePhase: (phaseId: string) => void;
  selectedFeatureId: string | null;
  onSelectFeature: (feature: FeatureWithStatus) => void;
  /**
   * Bật chế độ chọn hàng loạt (checkbox) — chỉ Admin (FEATURE_CONFIRM_BULK)
   * mới truyền các prop này, người khác dùng cây như cũ.
   */
  bulkSelectedIds?: Set<string>;
  onToggleBulkSelect?: (feature: FeatureWithStatus) => void;
}

function StatusIcon({ status }: { status: FeatureWithStatus['acceptance_status'] }): JSX.Element {
  if (status === 'CONFIRMED') {
    return <CheckCircleIcon fontSize="small" color="success" />;
  }
  if (status === 'CHANGE_REQUESTED') {
    return <ReportProblemIcon fontSize="small" color="warning" />;
  }
  return <RadioButtonUncheckedIcon fontSize="small" color="disabled" />;
}

function FeatureRowItem({
  feature,
  selected,
  onSelect,
  secondary,
  bulkChecked,
  onToggleBulkSelect,
}: {
  feature: FeatureWithStatus;
  selected: boolean;
  onSelect: () => void;
  secondary?: string;
  bulkChecked?: boolean;
  onToggleBulkSelect?: () => void;
}): JSX.Element {
  // Còn bug mở → hiển thị "Fixing" thay cho trạng thái gốc của tính năng.
  const isFixing = (feature.open_bug_count ?? 0) > 0;
  const statusMeta = isFixing ? FEATURE_FIXING_META : FEATURE_STATUS_META[feature.status];
  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{ borderRadius: 1, py: 0.5 }}
      // Dòng tính năng và nút giai đoạn đều là ListItemButton nên cùng class
      // MUI; e2e cần phân biệt được hai loại để đếm đúng số dòng tính năng.
      data-testid="feature-row"
    >
      {onToggleBulkSelect && (
        <Checkbox
          size="small"
          checked={bulkChecked ?? false}
          onClick={(e) => e.stopPropagation()}
          onChange={onToggleBulkSelect}
          sx={{ p: 0.5, mr: 0.5 }}
        />
      )}
      <ListItemIcon sx={{ minWidth: 28 }}>
        <StatusIcon status={feature.acceptance_status} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span">{feature.name}</Box>
            <BugHistoryIcon
              totalBugCount={feature.total_bug_count}
              openBugCount={feature.open_bug_count}
            />
          </Box>
        }
        secondary={secondary}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
      <Chip
        label={statusMeta.label}
        color={statusMeta.color}
        size="small"
        variant={isFixing ? 'filled' : 'outlined'}
        sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
      />
    </ListItemButton>
  );
}

/** Cây điều hướng Giai đoạn → Tính năng, tải lười theo giai đoạn, có tìm kiếm phía server. */
export function PhaseFeatureTree({
  projectId,
  phases,
  featuresByPhase,
  loadingPhases,
  loadingFeatures,
  errorByPhase,
  openPhaseIds,
  onTogglePhase,
  selectedFeatureId,
  onSelectFeature,
  bulkSelectedIds,
  onToggleBulkSelect,
}: PhaseFeatureTreeProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [searchHits, setSearchHits] = useState<FeatureSearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const trimmedSearch = search.trim();

  // Tìm kiếm chạy trên server vì cây tải lười — giai đoạn chưa mở thì trình
  // duyệt chưa có dữ liệu để lọc. Chờ 300ms sau lần gõ cuối mới gọi.
  // Cờ `cancelled` được khai báo ở phạm vi effect (không phải bên trong
  // setTimeout) và hàm dọn dẹp trả về trực tiếp từ effect, để React thực sự
  // gọi nó khi trimmedSearch/projectId đổi hoặc component unmount — nhờ đó
  // hủy được cả timer đang chờ lẫn kết quả đang bay về giữa chừng.
  useEffect(() => {
    if (trimmedSearch.length === 0) {
      setSearchHits(null);
      setSearchError(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const { items } = await featureApi.searchInProject(projectId, trimmedSearch, { limit: 200 });
          if (!cancelled) {
            setSearchHits(items);
            setSearchError(null);
          }
        } catch {
          if (!cancelled) {
            setSearchHits([]);
            setSearchError('Không thể tìm kiếm lúc này.');
          }
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmedSearch, projectId]);

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Tìm tính năng theo tên hoặc mô tả…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {searchError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {searchError}
        </Alert>
      )}

      {searchHits !== null ? (
        // Chế độ tìm kiếm: danh sách phẳng toàn dự án, kèm tên giai đoạn.
        searching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : searchHits.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Không tìm thấy tính năng nào khớp từ khoá.
          </Typography>
        ) : (
          <List dense disablePadding>
            {searchHits.map((hit) => (
              <FeatureRowItem
                key={hit.id}
                feature={hit}
                secondary={hit.phase_name}
                selected={hit.id === selectedFeatureId}
                onSelect={() => onSelectFeature(hit)}
              />
            ))}
          </List>
        )
      ) : loadingPhases ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : phases.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Dự án chưa có giai đoạn nào.
        </Typography>
      ) : (
        <List dense disablePadding>
          {phases.map((phase) => {
            const isOpen = openPhaseIds.includes(phase.id);
            const features = featuresByPhase[phase.id];
            const isLoading = loadingFeatures[phase.id] === true;
            const phaseError = errorByPhase[phase.id];
            return (
              <Box key={phase.id} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => onTogglePhase(phase.id)}
                  sx={{ borderRadius: 1 }}
                  data-testid={`phase-toggle-${phase.id}`}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FolderOpenIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={phase.name}
                    primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                  />
                  {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List dense disablePadding sx={{ pl: 2 }}>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : phaseError ? (
                      <Typography variant="caption" color="error" sx={{ pl: 2 }}>
                        {phaseError}
                      </Typography>
                    ) : features === undefined || features.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                        Giai đoạn này chưa có tính năng nào.
                      </Typography>
                    ) : (
                      features.map((feature) => (
                        <FeatureRowItem
                          key={feature.id}
                          feature={feature}
                          selected={feature.id === selectedFeatureId}
                          onSelect={() => onSelectFeature(feature)}
                          bulkChecked={bulkSelectedIds?.has(feature.id)}
                          onToggleBulkSelect={
                            onToggleBulkSelect ? () => onToggleBulkSelect(feature) : undefined
                          }
                        />
                      ))
                    )}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}
    </Box>
  );
}
